import * as React from 'react'

import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { ExecutionElement } from '../flow/ExecutionElement'
import { ElementLabel } from './ElementLabel'

import * as Flows from '../../../shared/domain/flows/types.no-deps'
import classNames from 'classnames'
import { SVG_Info } from '../svg/SVG_Info'
import { assumeExhaustion } from '../../../shared/lib/typeHelpers.no-deps'
import { RichTextNode } from '../../../shared/domain/richText/TextNode'
import type { ParagraphDescendant, RichTextParagraph } from '../../../shared/domain/richText/types.no-deps'
import type { SupportedLanguage } from '../../../shared/i18n/i18n.no-deps'
import type { LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'
import type { UnitAttribute, UnitData } from '../../../shared/domain/units/types.no-deps'
import { isUnitAttributeNode } from '../../../shared/domain/richText/helpers.no-deps'

interface Props {
    deps: CommonDeps
    element: Flows.HtmlTextContentElement | Flows.RichTextContentElement
    loopPathIds: LoopPathIds
    subjectData: UnitData
    openOptions?: () => void
}

export class ElementTextContent extends React.Component<Props, {}> {
    public render = () => renderCatch(this.props.deps, this.tryRender)
    private getIconColor = (displayType: Flows.DisplayTypeOptions) => {
        switch (displayType) {
            case 'info':
                return 'var(--bluey-grey)'
            case 'warning':
                return 'var(--bright-orange)'
            case 'problem':
                return 'var(--red)'
            default:
                assumeExhaustion(displayType)
        }
    }

    private tryRender = () => {
        const { element, subjectData, deps } = this.props

        return (
            <ExecutionElement
                deps={this.props.deps}
                elementId={element.id}
                loopPathIds={this.props.loopPathIds}
                validationMessages={[]}
            >
                <ElementLabel
                    required={false}
                    label={element.label}
                    validationMessages={[]}
                    openOptions={this.props.openOptions}
                />
                <div className={classNames(['AppCore__flow__ElementTextContent', element.displayType])}>
                    {element.displayType && (
                        <div className="AppCore__flow__ElementTextContent__icon">
                            <SVG_Info color={this.getIconColor(element.displayType)} />
                        </div>
                    )}
                    {TextContent(element, subjectData, deps.language)}
                </div>
            </ExecutionElement>
        )
    }
}

export function TextContent(element: Flows.TextContentElement, subjectData: UnitData, language: SupportedLanguage) {
    return Flows.textContentHasRichText(element)
        ? RichTextContent(element.richText, subjectData, language)
        : HtmlTextContent(element.sanitizedHTML, subjectData)
}

function HtmlTextContent(sanitizedHTML: string, subjectData: UnitData) {
    const convertedHtml = subjectData ? convertUnitAttributeValues(sanitizedHTML, subjectData) : sanitizedHTML
    return (
        <div className="AppCore__flow__ElementTextContent__text" dangerouslySetInnerHTML={{ __html: convertedHtml }} />
    )
}

function RichTextContent(richTextParagraphs: RichTextParagraph[], subjectData: UnitData, language: SupportedLanguage) {
    return (
        <div className="AppCore__flow__ElementTextContent__text">
            {richTextParagraphs
                .map(({ children }, i) =>
                    children.map((node, j) => (
                        <RichTextNode
                            key={`${i}-${j}`}
                            users={[]}
                            unitAttributes={toUnitAttributes(subjectData, node)}
                            {...node}
                            element={node}
                            language={language}
                        />
                    ))
                )
                .reduce((acc, curr, i) => [...acc, newParagraphHtml(i), ...curr])}
        </div>
    )
}

function newParagraphHtml(key: React.Key) {
    return <br key={key} />
}

function convertUnitAttributeValues(sanitizedHTML: string, subjectData?: UnitData) {
    let unitAttributesConverted = sanitizedHTML
    const unitAttributesToResolve = Object.entries(subjectData).filter(([key, value]) => !!value && key !== 'image')
    for (const [key, value] of unitAttributesToResolve) {
        unitAttributesConverted = unitAttributesConverted.replace(new RegExp(`\\[${key}\\]`, 'g'), `${value}`)
    }
    return unitAttributesConverted
}

function toUnitAttributes(unitData: UnitData | undefined, node: ParagraphDescendant): UnitAttribute[] {
    // Finding unitType from ParagraphDescendant shouldn't be necessary and is working around a safeguard in RichTextNode
    // It specifically checks whether the unitData was from a unit with the right unit type
    // Hopefully we shouldn't have got here if it wasn't and it's not worth storing the unit type id to prove it
    const unitTypeId = isUnitAttributeNode(node) ? node.unitTypeId : -1

    return unitData
        ? Object.entries(unitData).map(([key, value]) => ({ unitTypeId, name: key, value: `${value}` }))
        : []
}
